import React, { FC, useEffect, useMemo, useState } from 'react'
import Image, { ImageProps } from 'next/image'
import { useKeenSlider } from 'keen-slider/react'
import 'keen-slider/keen-slider.min.css'

import ActionButton from '../ActionButton'
import { colors } from '../../theme'

import * as SC from './styled'

export type StepByStepProps = {
  className?: string
  isOpen: boolean
  backgroundImageMobile?: ImageProps
  backgroundColor?: string
  onClose?: () => void
  textClose?: string
  textNext?: string
  textPrevious?: string
  buttonColor?: string
}

const StepByStep: FC<StepByStepProps> = (props) => {
  const {
    className,
    children,
    isOpen = false,
    backgroundImageMobile,
    backgroundColor,
    onClose,
    textClose,
    textNext,
    textPrevious,
    buttonColor,
  } = props

  const [isSliderReady, setSliderReady] = useState(false)
  const [currentSlide, setCurrentSlide] = React.useState(0)
  const [sliderRef, slider] = useKeenSlider<HTMLDivElement>({
    initial: 0,
    spacing: 20,
    slideChanged(s) {
      setCurrentSlide(s.details().relativeSlide)
    },
    created() {
      setSliderReady(true)
    },
    destroyed() {
      setSliderReady(false)
    },
  })

  const renderSlides = useMemo(() => {
    return (
      children &&
      React.Children.map(children, (child, index) => (
        <SC.Slide key={`slide-${index}`} className="keen-slider__slide">
          <SC.BigNumber>{index + 1}</SC.BigNumber>
          {child}
        </SC.Slide>
      ))
    )
  }, [children])

  const renderDots = useMemo(() => {
    return (
      slider &&
      children &&
      React.Children.map(children, (_child, index) => (
        <SC.Dot
          key={`dot-${index}`}
          onClick={() => {
            slider.moveToSlideRelative(index)
          }}
          isActive={currentSlide === index}
        >
          {index + 1}
        </SC.Dot>
      ))
    )
  }, [children, currentSlide, slider])

  useEffect(() => {
    window && isOpen === true
      ? (document.body.style.overflow = 'hidden')
      : (document.body.style.overflow = '')

    return () => {
      document.body.style.overflow = ''
    }
  }, [isOpen])

  return (
    <SC.Container
      className={className}
      role="dialog"
      hidden={!isOpen}
      aria-hidden={!isOpen}
      isOpen={isOpen}
      backgroundColor={backgroundColor}
    >
      <SC.Wrapper>
        <SC.Close>
          <ActionButton
            text={textClose}
            icon="close"
            variantType="button"
            variantColor={colors.redMandy}
            variantColorScheme="iconBlack"
            variantIcon="only"
            {...(buttonColor && { variantColor: buttonColor })}
            actionProps={{ onClick: onClose }}
          />
        </SC.Close>
        <SC.Content>
          <SC.Slider
            ref={sliderRef}
            className="keen-slider"
            isSliderReady={isSliderReady}
          >
            {renderSlides}
          </SC.Slider>
        </SC.Content>
      </SC.Wrapper>
      <SC.Controls>
        {slider && (
          <>
            <SC.SliderArrow
              text={textPrevious}
              icon="chevronLeft"
              variantType="button"
              variantColor={colors.redMandy}
              variantColorScheme="iconFullWhite"
              variantIcon="only"
              {...(buttonColor && { variantColor: buttonColor })}
              actionProps={{
                onClick: () => slider.prev(),
                disabled: currentSlide === 0,
              }}
            />
            <SC.Dots>{renderDots}</SC.Dots>
            <SC.SliderArrow
              text={textNext}
              icon="chevronRight"
              variantType="button"
              variantColor={colors.redMandy}
              variantColorScheme="iconFullWhite"
              variantIcon="only"
              {...(buttonColor && { variantColor: buttonColor })}
              actionProps={{
                onClick: () => slider.next(),
                disabled: currentSlide === slider.details().size - 1,
              }}
            />
          </>
        )}
      </SC.Controls>
      {backgroundImageMobile && (
        <SC.BackgroundMobile>
          <Image {...backgroundImageMobile} />
        </SC.BackgroundMobile>
      )}
    </SC.Container>
  )
}

export default StepByStep
